import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@mui/material';
import classNames from 'clsx';

import { RM3_DETAILS_SOURCE } from 'arbitrip-common/client/utils/HotelDealsRm3';
import { addUtmDataToUrl } from 'arbitrip-common/client/utils/UtmUtils';

import TranslationToggleNotice from '@/components/general/TranslationToggleNotice.jsx';
import { isIsracardCompany } from '@/utils/isCompany';
import { ReactComponent as DisclaimerIcon } from '@/img/hotel_page/disclaimer_icon.svg';

import Rm3PhotoCarousel from './Rm3PhotoCarousel';
import Rm3RoomSpecs from './Rm3RoomSpecs';
import Rm3DealRow from './Rm3DealRow';
import Rm3Lightbox from './Rm3Lightbox';
import RIBBON_CSS_VARIABLES from './rm3RibbonVariables';
import useRm3RoomTranslation from './useRm3RoomTranslation';
import useRm3Photos from './useRm3Photos';
import { IconAmenity, IconChevronDown, IconClose } from './Rm3Icons';

// Debug only, untranslated as today (`RoomsCatalogDialog`): the catalog room's Expedia name and id.
const DEBUG_CATALOG_PREFIX = '- Expedia: ';
const EXPEDIA_PARTNER_PATH = 'company.partners_ui.expedia_partner';
const LIGHTBOX_TAP_GUARD_MS = 500;
// Must match `$rm3-details-amenities-collapsed` (4 rows × 32px).
const AMENITIES_COLLAPSED_HEIGHT = 128;
// Today's rule: a group room with fewer amenities than this shows the images-illustrative disclaimer.
const IMAGES_ILLUSTRATIVE_MAX_AMENITIES = 6;
const MIN_PHOTOS_FOR_THUMBS = 2;
const THUMB_SCROLL_MARGIN = 8;
const CLOSE_ICON_SIZE = 22;
const AMENITY_ICON_SIZE = 20;

/** Own strip (the gallery's built-in thumbnails carry fixed English aria-labels); scrolls itself, never the modal. */
function Rm3Thumbnails({ images, index, onSelect, onImageError }) {
    const { t } = useTranslation();
    const stripRef = useRef(null);

    useEffect(() => {
        const strip = stripRef.current;
        const thumb = strip && strip.children[index];
        if (!thumb) {
            return;
        }
        // Physical rect deltas on `scrollLeft` hold in RTL too; `scrollIntoView` would also scroll the modal body.
        const stripRect = strip.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
        if (thumbRect.left < stripRect.left) {
            strip.scrollLeft -= stripRect.left - thumbRect.left + THUMB_SCROLL_MARGIN;
        } else if (thumbRect.right > stripRect.right) {
            strip.scrollLeft += thumbRect.right - stripRect.right + THUMB_SCROLL_MARGIN;
        }
    }, [index]);

    return (
        <div className="rm3-details__thumbs" ref={stripRef} data-testid="rm3-room-details-thumbs">
            {images.map((url, position) => (
                <button
                    key={`${position}-${url}`}
                    type="button"
                    className={classNames('rm3-details__thumb', { 'rm3-details__thumb--on': position === index })}
                    aria-pressed={position === index}
                    aria-label={t('HotelDealsRm3.ShowPhoto', { number: position + 1 })}
                    onClick={() => onSelect(position)}
                    data-testid="rm3-room-details-thumb"
                >
                    <img src={url} alt="" loading="lazy" onError={() => onImageError(url)} />
                </button>
            ))}
        </div>
    );
}

/** Spec P2.3 / A5: one HTML block, as today. A group room adds its title as a bold first line. */
function aboutContentOf(details, description) {
    const room = details.room || {};
    const isGroup = details.source === RM3_DETAILS_SOURCE.GROUP;
    const hasParagraph = isGroup && !!room.roomDescriptionParagraph;
    // Without a paragraph, the group title is what was sent for translation (spec P2.9).
    return {
        title: (isGroup && (hasParagraph ? room.roomDescriptionTitle : description)) || '',
        html: (!isGroup || hasParagraph ? description : '') || '',
    };
}

function Rm3About({ title, html, headingOptions }) {
    const { t } = useTranslation();
    return (
        <section className="rm3-details__pad rm3-details__section" data-testid="rm3-room-details-about">
            <h3 className="rm3-details__heading">{t('HotelDealsRm3.AboutThisRoom', headingOptions)}</h3>
            <div className="rm3-details__about-body">
                {title && (
                    <p>
                        <strong>{title}</strong>
                    </p>
                )}
                {html && <div dangerouslySetInnerHTML={{ __html: html }} />}
            </div>
        </section>
    );
}

/** Spec P2.5: every amenity in a 4-row grid; the toggle shows only when the grid overflows (P2.18 Q6). */
function Rm3AmenityGrid({ amenities, englishNames, headingOptions }) {
    const { t } = useTranslation();
    const gridId = useId();
    const gridRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [fullHeight, setFullHeight] = useState(0);

    useLayoutEffect(() => {
        const measure = () => {
            if (gridRef.current) {
                setFullHeight(gridRef.current.scrollHeight);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [amenities]);

    if (!amenities.length) {
        return null;
    }
    const overflows = fullHeight > AMENITIES_COLLAPSED_HEIGHT;
    return (
        <section className="rm3-details__pad rm3-details__section">
            <h3 className="rm3-details__heading">{t('HotelDealsRm3.RoomAmenities', headingOptions)}</h3>
            <div
                id={gridId}
                ref={gridRef}
                className="rm3-details__amenities"
                style={expanded && overflows ? { maxHeight: fullHeight } : undefined}
                data-testid="rm3-room-details-amenities"
            >
                {amenities.map((name, index) => (
                    <span className="rm3-details__amenity" key={`${index}-${name}`}>
                        <IconAmenity name={englishNames[index] || name} size={AMENITY_ICON_SIZE} />
                        <bdi>{name}</bdi>
                    </span>
                ))}
            </div>
            {overflows && (
                <button
                    type="button"
                    className="rm3-details__toggle"
                    aria-expanded={expanded}
                    aria-controls={gridId}
                    onClick={() => setExpanded((previous) => !previous)}
                    data-testid="rm3-amenities-toggle"
                >
                    {expanded ? t('HotelDealsRm3.Hide') : t('ShowMore')}
                    <IconChevronDown
                        className={classNames('rm3-filters-toggle__chevron', {
                            'rm3-filters-toggle__chevron--open': expanded,
                        })}
                    />
                </button>
            )}
        </section>
    );
}

/**
 * ROOM DETAILS MODAL for RM3 rooms (spec-v2 Phase 2). One instance per page, owned by `HotelDealsRm3`, mounted
 * fresh for every open. Header = the room's name (never "Room details"); "Choose your deal" renders the opened
 * section's filtered deals with the page's `Rm3DealRow` and context, so no price is picked or computed here.
 */
function Rm3RoomDetailsDialog({
    open,
    onClose,
    container,
    unfilteredContainer,
    meta,
    deals,
    startIndex,
    brokenPhotos,
    onPhotoBroken,
    ribbons,
    displayOrder,
    context,
    profile,
    debugMode,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const galleryRef = useRef(null);
    const lastSlideAt = useRef(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const { details } = container;
    const translation = useRm3RoomTranslation(details, profile);
    // Carousel, thumbnails and viewer browse one working list, so the shared index names the same photo in each.
    const photos = useRm3Photos(details.images, { startIndex, initialBroken: brokenPhotos, onBroken: onPhotoBroken });

    const photoCount = photos.photos.length;
    const shownIndex = photos.index;
    // Every photo broken while the viewer is up: back to the no-photo state, and a later recheck must not reopen it.
    const showLightbox = lightboxOpen && photoCount > 0;
    if (lightboxOpen && photoCount === 0) {
        setLightboxOpen(false);
    }
    const isCatalog = details.source === RM3_DETAILS_SOURCE.CATALOG;
    const firstCard = (unfilteredContainer || container).cards[0];
    const firstCardMeta = firstCard ? meta.cards.get(firstCard.cardId) : null;
    const name = container.label || (firstCardMeta && firstCardMeta.title) || '';
    const showImagesIllustrative =
        details.source === RM3_DETAILS_SOURCE.GROUP && translation.amenities.length < IMAGES_ILLUSTRATIVE_MAX_AMENITIES;
    const showDependsOnAvailability = isCatalog && _.get(profile, EXPEDIA_PARTNER_PATH, false);
    const about = aboutContentOf(details, translation.description);
    const hasAbout = !!(about.title || about.html);

    const { setIndex: setPhotoIndex } = photos;
    const handleSlide = useCallback(
        (index) => {
            lastSlideAt.current = Date.now();
            setPhotoIndex(index);
        },
        [setPhotoIndex],
    );

    const selectPhoto = (index) => {
        handleSlide(index);
        if (galleryRef.current) {
            galleryRef.current.slideToIndex(index);
        }
    };

    // Spec P2.7: a click right after a slide change (a swipe) does not open the viewer.
    const openLightbox = () => {
        if (Date.now() - lastSlideAt.current >= LIGHTBOX_TAP_GUARD_MS) {
            setLightboxOpen(true);
        }
    };

    const closeLightbox = (index) => {
        setLightboxOpen(false);
        setPhotoIndex(index);
        if (galleryRef.current) {
            galleryRef.current.slideToIndex(index);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                scroll="paper"
                aria-labelledby={titleId}
                PaperProps={{
                    className: classNames('rm3-details__paper', { 'rm3-details__paper--behind': showLightbox }),
                }}
                BackdropProps={{ className: 'rm3-details__backdrop' }}
            >
                <div
                    className="rm3-details rm3-details--dialog"
                    style={RIBBON_CSS_VARIABLES}
                    data-testid="rm3-room-details"
                >
                    <div className="rm3-details__head">
                        <div className="rm3-details__title-column">
                            <h2 id={titleId} className="rm3-details__title" data-testid="rm3-room-details-title">
                                <bdi>{name}</bdi>
                            </h2>
                            {debugMode && isCatalog && (
                                <div className="rm3-details__debug" dir="ltr" data-testid="rm3-room-details-debug">
                                    {`${DEBUG_CATALOG_PREFIX}${details.room.name}(${details.room.id})`}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="rm3-details__close"
                            aria-label={t('HotelDealsRm3.Close')}
                            onClick={onClose}
                            data-testid="rm3-room-details-close"
                        >
                            <IconClose size={CLOSE_ICON_SIZE} />
                        </button>
                    </div>

                    <div className="rm3-details__body">
                        <div className="rm3-details__top">
                            <div className="rm3-details__gallery">
                                <Rm3PhotoCarousel
                                    images={photos.photos}
                                    alt={name}
                                    onOpen={photoCount > 0 ? openLightbox : null}
                                    startIndex={shownIndex}
                                    onSlide={handleSlide}
                                    onImageError={photos.markBroken}
                                    galleryRef={galleryRef}
                                    className="rm3-photo--details"
                                    showCount={false}
                                />
                                {photoCount >= MIN_PHOTOS_FOR_THUMBS && (
                                    <Rm3Thumbnails
                                        images={photos.photos}
                                        index={shownIndex}
                                        onSelect={selectPhoto}
                                        onImageError={photos.markBroken}
                                    />
                                )}
                                {showImagesIllustrative && (
                                    <div className="rm3-details__disclaimer">
                                        <span>{t('ImagesIllustrative')}.&nbsp;</span>
                                        <a
                                            href={addUtmDataToUrl(t('TermsAndConditionsUrl'), isIsracardCompany())}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t('SeeTCForDetails')}
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="rm3-details__specs">
                                <Rm3RoomSpecs
                                    details={details}
                                    bedText={meta.multiCard ? null : meta.singleBedText}
                                    bedKind={meta.singleBedKind}
                                />
                            </div>
                            <TranslationToggleNotice
                                translatedExists={translation.translatedExists}
                                showOriginal={translation.showOriginal}
                                onToggle={translation.toggleOriginal}
                                style={{ marginTop: 0 }}
                            />
                        </div>

                        {(hasAbout || translation.amenities.length > 0) && (
                            <div className="rm3-details__translatable" dir={translation.dir}>
                                <div
                                    className={classNames('rm3-details__translatable-content', {
                                        'rm3-details__translatable-content--hidden': translation.translating,
                                    })}
                                >
                                    {hasAbout && (
                                        <Rm3About
                                            title={about.title}
                                            html={about.html}
                                            headingOptions={translation.headingOptions}
                                        />
                                    )}
                                    <Rm3AmenityGrid
                                        amenities={translation.amenities}
                                        englishNames={details.amenities}
                                        headingOptions={translation.headingOptions}
                                    />
                                </div>
                                {translation.translating && (
                                    <div className="rm3-details__loader">
                                        <i className="fa fa-spin fa-spinner fa-2x" />
                                    </div>
                                )}
                            </div>
                        )}

                        {showDependsOnAvailability && (
                            <div className="rm3-details__pad rm3-details__disclaimer rm3-details__disclaimer--icon">
                                <DisclaimerIcon />
                                <div>{t('DependsOnAvailability')}</div>
                            </div>
                        )}

                        {deals.length > 0 && (
                            <section className="rm3-details__pad rm3-details__section">
                                <h3 className="rm3-details__heading">{t('HotelDealsRm3.ChooseYourDeal')}</h3>
                                <div className="rm3-room__list" data-testid="rm3-room-details-deals">
                                    {deals.map((deal) => {
                                        const cardId = meta.cardIdByDealId.get(deal.id);
                                        const cardMeta = meta.cards.get(cardId) || {};
                                        return (
                                            <Rm3DealRow
                                                key={deal.id}
                                                deal={deal}
                                                ribbon={ribbons.get(deal.id)}
                                                roomName={cardMeta.title}
                                                cardId={cardId}
                                                bedText={cardMeta.bedText}
                                                bedKind={cardMeta.bedKind}
                                                displayedOrder={displayOrder.get(deal.id)}
                                                context={context}
                                            />
                                        );
                                    })}
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            </Dialog>
            {showLightbox && (
                <Rm3Lightbox
                    images={photos.photos}
                    alt={name}
                    index={shownIndex}
                    onSlide={setPhotoIndex}
                    onImageError={photos.markBroken}
                    onClose={closeLightbox}
                />
            )}
        </>
    );
}

export default Rm3RoomDetailsDialog;
